// H5 移动端外壳：顶部标题栏 + 底部 5-Tab 导航
// 所有真实 H5 页面统一用此外壳包裹，保证一致的苹果官网风格
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const TABS = [
  {
    key: 'workbench', path: '/h5/workbench', label: '工作台',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12l9-9 9 9" /><path d="M5 10v10h14V10" />
      </svg>
    ),
  },
  {
    key: 'samples', path: '/h5/samples', label: '样品',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 7l-8-4-8 4 8 4 8-4z" /><path d="M4 7v10l8 4 8-4V7" /><path d="M12 11v10" />
      </svg>
    ),
  },
  {
    key: 'approval', path: '/h5/approval', label: '审批',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
  {
    key: 'data', path: '/h5/data', label: '数据',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-5" />
      </svg>
    ),
  },
  {
    key: 'profile', path: '/h5/profile', label: '我的',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
      </svg>
    ),
  },
];

export default function H5Shell({ children, title }: { children: React.ReactNode; title?: string }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('h5-mode');
    const vp =
      document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null ||
      (() => {
        const m = document.createElement('meta');
        m.name = 'viewport';
        document.head.appendChild(m);
        return m;
      })();
    vp.content = 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover';
    return () => { document.body.classList.remove('h5-mode'); };
  }, []);

  const current = location.pathname;
  const matchedTab = TABS.find((t) => current.startsWith(t.path));
  const pageTitle = title || matchedTab?.label || '样品管理';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--apple-bg)', display: 'flex', flexDirection: 'column' }}>
      {/* 顶部导航栏（苹果风格毛玻璃） */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(255,255,255,0.94)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          borderBottom: '0.5px solid var(--apple-separator)',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--apple-dark)',
        }}
      >
        {pageTitle}
      </div>

      {/* 主体内容（底部留出 Tab 高度） */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 70 }}>{children}</div>

      {/* 底部 5-Tab */}
      <nav
        className="h5-bottom-nav"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          display: 'flex',
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          borderTop: '0.5px solid var(--apple-separator)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingTop: '4px',
          // 移动端不允许任何 CSS transform 干扰定位
          transform: 'none',
        }}
      >
        {TABS.map((t) => {
          const active = current.startsWith(t.path);
          return (
            <button
              key={t.key}
              type="button"
              className={`h5-tab-item ${active ? 'active' : ''}`}
              onClick={() => navigate(t.path)}
              style={{
                flex: '1 1 0',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                padding: '6px 2px 4px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: active ? '#0071e3' : 'var(--apple-gray)',
                transition: 'color 0.15s ease',
              }}
            >
              <div className="h5-tab-icon" style={{ transform: 'scale(0.9)' }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: active ? 600 : 500, whiteSpace: 'nowrap' }}>{t.label}</div>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
